import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Search } from 'react-feather';
import { format, differenceInCalendarDays } from 'date-fns';
import { BannerContext } from '../../../InternalApiApp';
import Page, { PageContentBanner, PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import Table from '../../../components/Table/Table';
import VideoIndicator from '../../../components/Labels/VideoIndicator';
import SimpleSearchFilter, { filterBy } from '../../../components/Input/SimpleSearchFilter';
import { useRemote } from '../../../Utils/Utils';
import Select from 'react-select'
import { STATUS_CONFIG } from './CommentaryTrackerExportUtils';
import CommentaryTrackerExportButton from './CommentaryTrackerExportButton';

const statusOptions = [
    { value: 'ALL', label: 'All Statuses' },
    { value: 'COMPLETE', label: 'Complete' },
    { value: 'READY_TO_PUBLISH', label: 'Ready to Publish' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'NOT_STARTED', label: 'Not Started' }
];

export function getPreviousQuarter() {
	const now = new Date();
	const month = now.getMonth() + 1;
	const year = now.getFullYear();
	let prevQ, prevYear;
	if (month <= 3) { prevQ = 4; prevYear = year - 1; }
	else if (month <= 6) { prevQ = 1; prevYear = year; }
	else if (month <= 9) { prevQ = 2; prevYear = year; }
	else { prevQ = 3; prevYear = year; }
	return `Q${prevQ}-${prevYear}`;
}

export function getLast4Quarters() {
	const quarters = [];
	let quarter = getPreviousQuarter();
	for (let i = 0; i < 4; i++) {
		quarters.push(quarter);
		const [q, year] = [parseInt(quarter[1]), parseInt(quarter.slice(3))];
		quarter = q === 1 ? `Q4-${year - 1}` : `Q${q - 1}-${year}`;
	}
	return quarters;
}

export function StatusBadge({ status }) {
	const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED;
	return (
		<span style={{
			background: cfg.bg,
			color: cfg.text,
			borderRadius: 4,
			padding: '2px 8px',
			fontSize: '0.75rem',
			fontWeight: 600,
			whiteSpace: 'nowrap',
		}}>
			{cfg.label}
		</span>
	);
}

export function filterAndSort(rows, searchTerm, statusFilter, sortingColumn) {
	if (!rows) return [];
	let result = rows.filter(row => filterBy(row, ['extCompanyName', 'lastUpdatedBy'], searchTerm));
	if (statusFilter !== 'ALL') {
		result = result.filter(row => row.status === statusFilter);
	}
	result = [...result];
	const asc = sortingColumn.ascending;
	if (sortingColumn.column === 'Company') {
		result.sort((a, b) => asc
			? (a.extCompanyName || '').localeCompare(b.extCompanyName || '')
			: (b.extCompanyName || '').localeCompare(a.extCompanyName || ''));
	} else if (sortingColumn.column === 'Status') {
		result.sort((a, b) => asc
			? (a.status || '').localeCompare(b.status || '')
			: (b.status || '').localeCompare(a.status || ''));
	}
	return result;
}

function SummaryCard({ label, count, total, colour, isPercent, noBorderRight }) {
	const pct = isPercent ? count : (total > 0 ? Math.round((count / total) * 100) : 0);
	return (
		<div style={{
			flex: 1,
			padding: '14px 20px 12px 20px',
			display: 'flex',
			flexDirection: 'column',
			gap: 2,
			borderRight: noBorderRight ? 'none' : '1px solid #efefef',
			minWidth: 0,
		}}>
			<div style={{ fontSize: '0.65rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>
				{label}
			</div>
			<div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
				<span style={{ fontSize: '1.8rem', fontWeight: 700, color: colour, lineHeight: 1.1 }}>
					{isPercent ? `${count}%` : count}
				</span>
				{!isPercent && <span style={{ fontSize: '0.75rem', color: '#bbb' }}>of {total}</span>}
			</div>
			<div style={{ marginTop: 8, background: '#efefef', borderRadius: 2, height: 3 }}>
				<div style={{ background: colour, width: `${pct}%`, height: 3, borderRadius: 2 }} />
			</div>
		</div>
	);
}

const ExternalAccessCommentaryTracker = () => {
	const remote = useRemote();
	const { setBannerBreadCrumbs } = useContext(BannerContext);

	const [selectedQuarter, setSelectedQuarter] = useState(getPreviousQuarter());
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState('ALL');
	const [sortingColumn, setSortingColumn] = useState({ column: 'Company', ascending: true });

	const [expandedRows, setExpandedRows] = useState({});
	const [rowDetails, setRowDetails] = useState({});
	const [loadingDetails, setLoadingDetails] = useState({});

	const quarters = getLast4Quarters();

	useEffect(() => {
		setBannerBreadCrumbs({ tab: 'Commentary Tracker' });
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setExpandedRows({});
		setRowDetails({});
		remote.get(`/crm/ext/access/commentary/tracker?quarter=${selectedQuarter}`).then(resp => {
			if (!cancelled) {
				setData(resp);
				setLoading(false);
			}
		}).catch(() => {
			if (!cancelled) {
				setData(null);
				setLoading(false);
			}
		});
		return () => { cancelled = true; };
	}, [selectedQuarter]);

	// Fetch (and cache) a company's portfolio detail. Shared by row-expand and the CSV export.
	const fetchDetail = useCallback((externalAccessId) => {
		return remote.get(`/crm/ext/access/commentary/tracker/${externalAccessId}?quarter=${selectedQuarter}`)
			.then(detail => {
				setRowDetails(prev => ({ ...prev, [externalAccessId]: detail }));
				return detail;
			});
	}, [remote, selectedQuarter]);

	function toggleExpand(externalAccessId) {
		const isOpen = !!expandedRows[externalAccessId];
		if (!isOpen && !rowDetails[externalAccessId]) {
			setLoadingDetails(prev => ({ ...prev, [externalAccessId]: true }));
			fetchDetail(externalAccessId)
				.then(() => setLoadingDetails(prev => ({ ...prev, [externalAccessId]: false })))
				.catch(() => setLoadingDetails(prev => ({ ...prev, [externalAccessId]: false })));
		}
		setExpandedRows(prev => ({ ...prev, [externalAccessId]: !isOpen }));
	}

	function toggleSort(column) {
		const ascending = sortingColumn.column === column ? !sortingColumn.ascending : true;
		setSortingColumn({ column, ascending });
	}

	const summary = data && data.summary;
	const companies = filterAndSort(data && data.companies, searchTerm, statusFilter, sortingColumn);

	const deadlineDate = data && data.deadline ? new Date(data.deadline) : null;
	const today = new Date();
	const deadlinePassed = deadlineDate && deadlineDate < today;
	const daysRemaining = deadlineDate && !deadlinePassed ? differenceInCalendarDays(deadlineDate, today) : null;
	const notStartedCount = summary ? summary.notStarted : 0;

	const colWidths = ['3%', '28%', '17%', '17%', '20%', '15%'];
	const headers = [
		{ text: <></> },
		{
			text: (
				<div className='sorting-header' onClick={() => toggleSort('Company')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
					Company
					{sortingColumn.column === 'Company' && sortingColumn.ascending
						? <ChevronUp size={12} className="colour-text-primary" />
						: <ChevronDown size={12} className={sortingColumn.column === 'Company' ? 'colour-text-primary' : 'colour-text-neutral-dark-30'} />}
				</div>
			)
		},
		{ text: 'Portfolios' },
		{
			text: (
				<div className='sorting-header' onClick={() => toggleSort('Status')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
					Status
					{sortingColumn.column === 'Status' && sortingColumn.ascending
						? <ChevronUp size={12} className="colour-text-primary" />
						: <ChevronDown size={12} className={sortingColumn.column === 'Status' ? 'colour-text-primary' : 'colour-text-neutral-dark-30'} />}
				</div>
			)
		},
		{ text: 'Last Updated' },
		{ text: 'Submitted' },
	];

	const dataMap = (rows) => rows.map(row => {
		const { externalAccessId, extCompanyName, lastUpdatedBy, lastUpdatedByEmail, submittedBy, submittedByEmail, portfoliosComplete, portfoliosAssigned, status, lastUpdatedAt, submittedAt } = row;
		const isNotStarted = status === 'NOT_STARTED';
		const progressPct = portfoliosAssigned > 0 ? Math.round((portfoliosComplete / portfoliosAssigned) * 100) : 0;
		const isExpanded = !!expandedRows[externalAccessId];
		const detail = rowDetails[externalAccessId];
		const isLoadingDetail = !!loadingDetails[externalAccessId];

		return (
			<React.Fragment key={externalAccessId}>
				<tr
					onClick={() => toggleExpand(externalAccessId)}
					style={{ cursor: 'pointer', ...(isNotStarted ? { background: '#FFFBFA' } : {}) }}
				>
					<td>
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							{isExpanded
								? <ChevronDown size={14} className="colour-text-primary" />
								: <ChevronRight size={14} className="colour-text-neutral-dark-30" />}
						</div>
					</td>
					<td style={{ paddingLeft: '0.5rem' }}>
						<div style={{ fontWeight: 500 }}>{extCompanyName}</div>
					</td>
					<td>
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<div style={{ background: '#e0e0e0', borderRadius: 4, height: 6, width: 60 }}>
								<div style={{ background: '#1B78F5', width: `${progressPct}%`, height: 6, borderRadius: 4 }} />
							</div>
							<span style={{ fontSize: '0.8rem' }}>{portfoliosComplete} / {portfoliosAssigned}</span>
						</div>
					</td>
					<td><StatusBadge status={status} /></td>
					<td>
						{lastUpdatedBy && <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{lastUpdatedBy}</div>}
						{lastUpdatedByEmail && <div style={{ fontSize: '0.75rem', color: '#888' }}>{lastUpdatedByEmail}</div>}
						{lastUpdatedAt && <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{format(new Date(lastUpdatedAt), 'dd/MM/yyyy HH:mm')}</div>}
					</td>
					<td>
						{submittedAt ? (
							<>
								{submittedBy && <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{submittedBy}</div>}
								{submittedByEmail && <div style={{ fontSize: '0.75rem', color: '#888' }}>{submittedByEmail}</div>}
								<div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{format(new Date(submittedAt), 'dd/MM/yyyy HH:mm')}</div>
							</>
						) : '—'}
					</td>
				</tr>
				{isExpanded && (
					isLoadingDetail ? (
						<tr>
							<td colSpan={6} style={{ textAlign: 'center', padding: '8px', fontSize: '0.8rem', color: '#888', background: '#f7f9fe' }}>Loading portfolios...</td>
						</tr>
					) : detail && detail.portfolios && detail.portfolios.length > 0 ? (
						detail.portfolios.map(p => (
							<tr key={`${externalAccessId}-${p.fundId}`} style={{ background: '#f7f9fe' }}>
								<td></td>
								<td style={{ paddingLeft: '1.5rem' }}>
									<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
										<span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{p.fundName || p.fundNumber}</span>
										{p.contentType === 'video' && (
											<span title={p.videoTranscript || 'Video commentary'} style={{ display: 'inline-flex' }}>
												<VideoIndicator />
											</span>
										)}
									</div>
									{p.fundName && p.fundNumber && <div style={{ fontSize: '0.75rem', color: '#888' }}>{p.fundNumber}</div>}
								</td>
								<td></td>
								<td><StatusBadge status={p.status} /></td>
								<td>
									{p.lastUpdatedBy && <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{p.lastUpdatedBy}</div>}
									{p.lastUpdatedByEmail && <div style={{ fontSize: '0.75rem', color: '#888' }}>{p.lastUpdatedByEmail}</div>}
									{p.lastUpdatedAt && <div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{format(new Date(p.lastUpdatedAt), 'dd/MM/yyyy HH:mm')}</div>}
									{!p.lastUpdatedBy && !p.lastUpdatedAt && <span style={{ color: '#aaa' }}>—</span>}
								</td>
								<td>
									{p.submittedAt ? (
										<>
											{p.submittedBy && <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{p.submittedBy}</div>}
											{p.submittedByEmail && <div style={{ fontSize: '0.75rem', color: '#888' }}>{p.submittedByEmail}</div>}
											<div style={{ fontSize: '0.75rem', color: '#666', marginTop: 2 }}>{format(new Date(p.submittedAt), 'dd/MM/yyyy HH:mm')}</div>
										</>
									) : '—'}
								</td>
							</tr>
						))
					) : detail ? (
						<tr>
							<td colSpan={6} style={{ textAlign: 'center', padding: '8px', fontSize: '0.8rem', color: '#888', background: '#f7f9fe' }}>No portfolio data</td>
						</tr>
					) : null
				)}
			</React.Fragment>
		);
	});

	return (
		<Page fixedBanner>
			<PageContentBanner divider>
				{/* Quarter selector */}
				<div style={{
					display: 'inline-flex',
					alignItems: 'center',
					border: '1px solid #e0e0e0',
					borderRadius: 24,
					padding: 3,
					background: '#fff',
					gap: 2,
				}}>
					{quarters.map(q => (
						<button
							key={q}
							onClick={() => setSelectedQuarter(q)}
							style={{
								padding: '4px 16px',
								borderRadius: 20,
								border: 'none',
								background: selectedQuarter === q ? '#1B78F5' : 'transparent',
								color: selectedQuarter === q ? '#fff' : '#888',
								cursor: 'pointer',
								fontSize: '0.8rem',
								fontWeight: selectedQuarter === q ? 700 : 400,
								whiteSpace: 'nowrap',
							}}
						>
							{q.replace('-', ' ')}
						</button>
					))}
				</div>
			</PageContentBanner>

			<PageContentBodyGrid id="commentary-tracker-page" rowGap="xl" gridTemplateRows="auto auto auto 1fr" paddingBottom='4.5rem'>

				{/* Deadline banner */}
				{deadlineDate && !deadlinePassed && (
					<PageGridItem col="1 / span 12">
						<div style={{
							background: '#FFF5E0',
							border: '1px solid #FADFA0',
							borderRadius: 6,
							padding: '10px 16px',
							color: '#D4850A',
							fontSize: '0.85rem',
							display: 'flex',
							gap: 16,
							alignItems: 'center',
						}}>
							<span>
								<strong>Deadline: {format(deadlineDate, 'dd MMM yyyy')}</strong>
								{daysRemaining !== null && ` — ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`}
							</span>
							{notStartedCount > 0 && (
								<span>{notStartedCount} compan{notStartedCount !== 1 ? 'ies' : 'y'} not yet started</span>
							)}
						</div>
					</PageGridItem>
				)}

				{/* Stats strip */}
				{summary && (
					<PageGridItem col="1 / span 12">
						<div style={{ display: 'flex', border: '1px solid #efefef', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
							<SummaryCard label="Complete"         count={summary.complete}          total={summary.total} colour="#22C7A0" />
							<SummaryCard label="Ready to Publish" count={summary.readyToPublish}    total={summary.total} colour="#5BBDE8" />
							<SummaryCard label="In Progress"      count={summary.inProgress}        total={summary.total} colour="#1B78F5" />
							<SummaryCard label="Not Started"      count={summary.notStarted}        total={summary.total} colour="#EF6B6B" />
							<SummaryCard label="Completion"       count={summary.completionPercent} total={100}           colour="#1B78F5" isPercent noBorderRight />
						</div>
					</PageGridItem>
				)}

				{/* Toolbar */}
				<PageGridItem col="1 / span 12">
					<div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
						<div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
							<SimpleSearchFilter
								size='small'
								width='xxl'
								placeholder={`Search (${companies.length})`}
								isClearable
								icon={<Search />}
								setSearchTerm={setSearchTerm}
								searchTerm={searchTerm}
							/>
							<Select
								value={statusOptions.find(opt => opt.value === statusFilter)}
								onChange={opt => setStatusFilter(opt.value)}
								options={statusOptions}
								isSearchable={false}
								styles={{
									control: (base) => ({
										...base,
										padding: '0 2px',
										borderRadius: 4,
										border: '1px solid #ccc',
										fontSize: '0.85rem',
										height: 32,
										minWidth: 180,
									}),
								}}
							/>
							{/* TODO: portfolio filter */}
						</div>
						<CommentaryTrackerExportButton
							companies={companies}
							quarter={selectedQuarter}
							rowDetails={rowDetails}
							fetchDetail={fetchDetail}
							loading={loading}
						/>
					</div>
				</PageGridItem>

				{/* Table */}
				<PageGridItem container col="1 / span 12">
					<Table
						minWidth={800}
						colWidths={colWidths}
						headers={headers}
						dataMap={dataMap(companies)}
						dataLoading={loading}
					/>
				</PageGridItem>

			</PageContentBodyGrid>
		</Page>
	);
};

export default ExternalAccessCommentaryTracker;
